import {
  DeliveryDiscountsService,
  type DeliveryDiscountBranchListedAdminDto,
  type DeliveryDiscountListedAdminDto,
  type PostDeliveryDiscountAdminRequest,
  type PutDeliveryDiscountRequestAdmin
} from '@/backend/market'
import { DeliveryDiscountRepository } from '../../domain/repositories/DeliveryDiscountRepository'
import type { DeliveryDiscountFilterEntity } from '../../domain/entities/DeliveryDiscountFilterEntity'
import { DeliveryDiscountFilterModel } from '../models/DeliveryDiscountFilterModel'
import { DeliveryBranchMiniModel } from '../models/DeliveryBranchMiniEntity'
import { PaginateEntity } from '@/shared/entities/PaginateEntity'
import { DeliveryDiscountModel } from '../models/DeliveryDiscountModel'
import type { CreateUpdateDeliveryDiscountEntity } from '../../domain/entities/CreateUpdateDeliveryDiscountEntity'
import { CreateUpdateDeliveryDiscountModel } from '../models/CreateUpdateDeliveryDiscountModel'
import type { BranchMiniEntity } from '@/features/branch/domain/entities/BranchMiniEntity'
import type { DeliveryDiscountBranchesFilterEntity } from '../../domain/entities/DeliveryDiscountBranchesFilterEntity'
import { DeliveryDiscountBranchesFilterModel } from '../models/DeliveryDiscountBranchesFilterModel'
import { DeliveryDiscountBranchesMiniModel } from '../models/DeliveryDiscountBranchesMiniModel'
import type { DeliveryDiscountBranchesMiniEntity } from '../../domain/entities/DeliveryBranchesMiniEntity'

export class DeliveryDiscountRepositoryImpl implements DeliveryDiscountRepository {
  async getDeliveryDiscounts(
    filterData: DeliveryDiscountFilterEntity
  ): Promise<PaginateEntity<DeliveryBranchMiniModel[]> | { error: true }> {
    const filterModel = new DeliveryDiscountFilterModel(filterData)
    try {
      const response = await DeliveryDiscountsService.getDeliveryDiscountsAdmin(filterModel)
      const deliveryDiscountMiniModels = response.items?.map(
        (item: DeliveryDiscountListedAdminDto) => new DeliveryBranchMiniModel(item)
      )
      const deliveryDiscountResponse = new PaginateEntity<DeliveryBranchMiniModel[]>(
        response,
        deliveryDiscountMiniModels
      )

      return deliveryDiscountResponse
    } catch {
      return { error: true }
    }
  }
  async getDeliveryDiscount(id: string): Promise<DeliveryDiscountModel | { error: true }> {
    try {
      const response = await DeliveryDiscountsService.getDeliveryDiscountAdmin({ id })
      return new DeliveryDiscountModel(response)
    } catch {
      return { error: true }
    }
  }
  async getDeliveryDiscountBranches(
    filterData: DeliveryDiscountBranchesFilterEntity
  ): Promise<PaginateEntity<DeliveryDiscountBranchesMiniEntity[]> | { error: true }> {
    const filterModel = new DeliveryDiscountBranchesFilterModel(filterData)
    try {
      const response = await DeliveryDiscountsService.getDeliveryDiscountBranchesAdmin(filterModel)

      const deliveryDiscountMiniModels = response.items?.map(
        (item: DeliveryDiscountBranchListedAdminDto) =>
          new DeliveryDiscountBranchesMiniModel(item.branch!)
      )
      const deliveryDiscountResponse = new PaginateEntity<DeliveryDiscountBranchesMiniModel[]>(
        response,
        deliveryDiscountMiniModels
      )

      return deliveryDiscountResponse
    } catch {
      return { error: true }
    }
  }
  async createDeliveryDiscount(
    data: CreateUpdateDeliveryDiscountEntity
  ): Promise<{ success: true } | { error: true }> {
    const createUpdateDeliveryDiscountModel = new CreateUpdateDeliveryDiscountModel(data)
    try {
      await DeliveryDiscountsService.postDeliveryDiscountAdmin({
        requestBody:
          createUpdateDeliveryDiscountModel as unknown as PostDeliveryDiscountAdminRequest
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }
  async updateDeliveryDiscount(
    id: string,
    data: CreateUpdateDeliveryDiscountEntity
  ): Promise<{ success: true } | { error: true }> {
    const createUpdateDeliveryDiscountModel = new CreateUpdateDeliveryDiscountModel(data)
    try {
      await DeliveryDiscountsService.putDeliveryDiscountAdmin({
        id,
        requestBody: createUpdateDeliveryDiscountModel as unknown as PutDeliveryDiscountRequestAdmin
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }
  async updateDeliveryDiscountBranches(
    id: string,
    branchIds: number[]
  ): Promise<{ success: true } | { error: true }> {
    try {
      await DeliveryDiscountsService.putDeliveryDiscountBranchesAdmin({
        id,
        requestBody: {
          branchIds
        }
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }
  async addDeliveryDiscountBranch(
    id: string,
    branch: BranchMiniEntity
  ): Promise<{ success: true } | { error: true }> {
    try {
      await DeliveryDiscountsService.postDeliveryDiscountBranchAdmin({
        id,
        branchId: branch.id!
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }
  async deleteDeliveryDiscountBranch(
    id: string,
    branch: BranchMiniEntity
  ): Promise<{ success: true } | { error: true }> {
    try {
      await DeliveryDiscountsService.deleteDeliveryDiscountBranchAdmin({
        id,
        branchId: branch.id!
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }
}
